import React from 'react';
import { Seat } from '../../interfaces/mealPlan';

interface SeatSelectorProps {
    seats: Seat[];
    activeSeatId: string | null;
    onSelectSeat: (seatId: string | null) => void;
    onAddSeat: () => void;
    onRemoveSeat?: (seatId: string) => void;
    itemCountPerSeat: Record<string, number>;
}

const SeatSelector: React.FC<SeatSelectorProps> = ({
    seats, activeSeatId, onSelectSeat, onAddSeat, onRemoveSeat, itemCountPerSeat,
}) => {
    if (seats.length === 0) return null;

    const totalItems = Object.values(itemCountPerSeat).reduce((a, b) => a + b, 0);

    return (
        <div style={{
            display: 'flex', gap: 6, padding: '8px 10px',
            overflowX: 'auto', backgroundColor: '#F9FAFB',
            borderBottom: '1px solid #EAECF0',
            flexWrap: 'wrap',
        }}>
            {/* All tab */}
            <button
                onClick={() => onSelectSeat(null)}
                style={{
                    padding: '6px 14px', borderRadius: 16, fontSize: 13, fontWeight: 600,
                    border: 'none',
                    backgroundColor: activeSeatId === null ? '#1643E0' : '#fff',
                    color: activeSeatId === null ? '#fff' : '#475467',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                    boxShadow: activeSeatId === null ? '0 2px 6px rgba(22,67,224,0.25)' : '0 1px 2px rgba(0,0,0,0.06)',
                    display: 'flex', alignItems: 'center', gap: 5,
                }}
            >
                All
                {totalItems > 0 && (
                    <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                        backgroundColor: activeSeatId === null ? 'rgba(255,255,255,0.25)' : '#EAECF0',
                        color: activeSeatId === null ? '#fff' : '#475467',
                    }}>{totalItems}</span>
                )}
            </button>

            {/* Seat tabs */}
            {seats.map((seat) => {
                const isActive = activeSeatId === seat.id;
                const isHotelGuest = seat.type === 'hotel_guest';
                const count = itemCountPerSeat[seat.id] || 0;
                const firstName = (seat.guestName || `Guest ${seat.seatNumber}`).split(' ')[0];

                return (
                    <div
                        key={seat.id}
                        role='button'
                        tabIndex={0}
                        onClick={() => onSelectSeat(seat.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectSeat(seat.id); } }}
                        style={{
                            padding: isActive && onRemoveSeat ? '6px 6px 6px 10px' : '6px 10px',
                            borderRadius: 16, fontSize: 12, fontWeight: 500,
                            border: 'none',
                            backgroundColor: isActive ? (isHotelGuest ? '#EEF4FF' : '#F2F4F7') : '#fff',
                            color: '#344054',
                            cursor: 'pointer', whiteSpace: 'nowrap',
                            boxShadow: isActive ? `0 0 0 2px ${isHotelGuest ? '#1643E0' : '#475467'}` : '0 1px 2px rgba(0,0,0,0.06)',
                            display: 'flex', alignItems: 'center', gap: 5,
                        }}
                    >
                        {/* Number circle */}
                        <span style={{
                            width: 20, height: 20, borderRadius: '50%', display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700,
                            backgroundColor: isActive ? (isHotelGuest ? '#1643E0' : '#475467') : (isHotelGuest ? '#D1DEFF' : '#E4E7EC'),
                            color: isActive ? '#fff' : (isHotelGuest ? '#1643E0' : '#475467'),
                            flexShrink: 0,
                        }}>
                            {seat.seatNumber}
                        </span>

                        {/* Name only */}
                        <span style={{ fontSize: 12, fontWeight: 600, maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {firstName}
                        </span>

                        {/* Item count */}
                        {count > 0 && (
                            <span style={{
                                fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                                backgroundColor: isActive ? '#1643E0' : '#EAECF0',
                                color: isActive ? '#fff' : '#475467',
                            }}>{count}</span>
                        )}

                        {/* Remove (only on the active tab) */}
                        {isActive && onRemoveSeat && (
                            <button
                                type='button'
                                aria-label={`Remove ${firstName}`}
                                title='Remove guest'
                                onClick={(e) => { e.stopPropagation(); onRemoveSeat(seat.id); }}
                                style={{
                                    width: 18, height: 18, borderRadius: '50%', border: 'none',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    backgroundColor: isHotelGuest ? '#D1DEFF' : '#E4E7EC',
                                    color: isHotelGuest ? '#1643E0' : '#475467',
                                    cursor: 'pointer', flexShrink: 0, padding: 0, lineHeight: 1,
                                }}
                            >
                                <svg width="8" height="8" viewBox="0 0 14 14" fill="none">
                                    <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        )}
                    </div>
                );
            })}

            {/* Add button */}
            <button
                onClick={onAddSeat}
                style={{
                    padding: '6px 10px', borderRadius: 16, fontSize: 14,
                    border: '1px dashed #D0D5DD', backgroundColor: 'transparent',
                    color: '#98A2B3', cursor: 'pointer', whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center',
                }}
            >+</button>
        </div>
    );
};

export default SeatSelector;
